import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Pagination,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { PermissionsI } from 'api/common.types';
import { getGroupsDropdown } from 'api/group/group.api';
import { getPermissionsDropdown } from 'api/permission/permission.api';
import { getProfilesDropdown } from 'api/profile/profile.api';
import { UserListQuery, UserStatus } from 'api/user/api.types';
import { blockUsers, unblockUsers } from 'api/user/user.api';
import ContainerPainel from 'components/container_painel';
import { ErrorDialog, OrderDialog, SuccessDialog } from 'components/shared';
import { FiltersDialog, ManageUserTerm, UserCard } from 'components/user';
import VerificaBloqueado from 'components/user/verifica_bloqueado';
import {
  ModuleEnum,
  PermissionType,
  usePermissions,
} from 'contexts/auth/permission_context';
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'routes/constants';
import useUsersStore, { IUser } from 'stores/users.store';
import LockRedIcon from '../../assets/icons/lock-red-icon.svg';
import UpdateIcon from '../../assets/icons/updated-icon.svg';

const ManageUser: React.FC = () => {
  const navigate = useNavigate();

  const { hasPermission } = usePermissions();
  const { users, infoUsers, getUsers } = useUsersStore();

  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [blockingUserIds, setBlockingUserIds] = useState<string[]>([]);
  const [unblockingUserIds, setUnblockingUserIds] = useState<string[]>([]);
  const [blockingReason, setBlockingReason] = useState<string>('');
  const [operationSuccessDialog, setOperationSuccessDialog] = useState(false);
  const [operationType, setOperationType] = useState<'block' | 'unblock'>(
    'block',
  );

  const [groupsDropdown, setGroupsDropdown] = useState<PermissionsI>();
  const [profilesDropdown, setProfilesDropdown] = useState<PermissionsI>();
  const [permissionsDropdown, setPermissionsDropdown] =
    useState<PermissionsI>();
  const [userStatusDropdown, setUserStatusDropdown] = useState<PermissionsI>();

  const [toggleOrderDialog, setToggleOrderDialog] = useState(false);
  const [toggleFiltersDialog, setToggleFiltersDialog] = useState(false);
  const [toggleConfirmUserBlockingDialog, setToggleConfirmUserBlockingDialog] =
    useState(false);
  const [
    toggleConfirmUserUnblockingDialog,
    setToggleConfirmUserUnblockingDialog,
  ] = useState(false);

  const [manageUserTermDialog, setManageUserTermDialog] = useState({
    open: false,
    userUuid: '',
    userName: '',
    close: () => setManageUserTermDialog((prev) => ({ ...prev, open: false })),
  });

  const [selectedSort, setSelectedSort] = useState<string>('recent');
  const [currentPage, setCurrentPage] = useState<number>(1);

  const [invalidRequest, setInvalidRequest] = useState(false);

  const handleCloseUnblockingDialog = () => {
    setUnblockingUserIds([]);
    setToggleConfirmUserUnblockingDialog(false);
  };

  const handleUnblockUsers = async () => {
    const Resp = await unblockUsers(unblockingUserIds);
    handleCloseUnblockingDialog();

    if (Resp.Status === 'OK') {
      setSelectAllChecked(false);
      setSelectedUsers([]);
      setOperationType('unblock');
      setOperationSuccessDialog(true);
      await getUsers(filters as UserListQuery);
    }
  };

  const [filters, setFilters] = useState<Partial<UserListQuery>>({
    page: '1',
    limit: '10',
    sort: 'DESC',
  });
  const [name, setName] = useState('');
  const [cpf, setCpf] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedProfile, setSelectedProfile] = useState('');
  const [selectedPermission, setSelectedPermission] = useState('');
  const [selectedUserStatus, setSelectedUserStatus] = useState('');

  useEffect(() => {
    const savedFilters = loadFiltersFromLocalStorage();
    if (savedFilters) {
      setFilters(savedFilters);
      setName(savedFilters.nome ?? '');
      setCpf(savedFilters.cpf ?? '');
      setSelectedGroup(savedFilters.groupUuid ?? '');
      setSelectedProfile(savedFilters.profileUuid ?? '');
      setSelectedPermission(savedFilters.permissionUuid ?? '');
      setSelectedUserStatus(savedFilters.status ?? '');
    }
  }, []);

  const hasAppliedFilters = () => {
    const { nome, cpf, groupUuid, profileUuid, permissionUuid, status } =
      filters;
    return !!(
      nome ??
      cpf ??
      groupUuid ??
      profileUuid ??
      permissionUuid ??
      status
    );
  };

  useEffect(() => {
    const fetchDropdowns = async () => {
      const permissions = await getPermissionsDropdown();
      setPermissionsDropdown(permissions);

      const profiles = await getProfilesDropdown();
      setProfilesDropdown(profiles);

      const groups = await getGroupsDropdown();

      setGroupsDropdown(groups);

      const statusOptions: PermissionsI = {
        data: Object.entries(UserStatus).map(([key, value]) => ({
          uuid: key,
          name: value,
        })),
      };

      setUserStatusDropdown(statusOptions);
    };
    fetchDropdowns();
  }, []);

  useEffect(() => {
    if (users) {
      const allSelected = users.every((user: IUser) =>
        selectedUsers?.includes(user.uuid),
      );
      setSelectAllChecked(allSelected);
    } else {
      setSelectAllChecked(false);
    }
  }, [users, selectedUsers]);

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked && users) {
      selectAllUsers();
    } else {
      clearAllSelection();
    }
  };

  const selectAllUsers = () => {
    const allUserIds = users.map((user: IUser) => user.uuid);
    setSelectedUsers(allUserIds);
  };

  const clearAllSelection = () => {
    setSelectedUsers([]);
  };

  const handleUserSelect =
    (userId: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = event.target;
      if (checked) {
        addUserToSelection(userId);
      } else {
        removeUserFromSelection(userId);
      }
    };

  const addUserToSelection = (userId: string) => {
    setSelectedUsers((prevSelected) => [...prevSelected, userId]);
  };

  const removeUserFromSelection = (userId: string) => {
    setSelectedUsers((prevSelected) => prevSelected.filter((id) => id !== userId));
  };

  const handleToggleBlockingDialog = (userIds: string[]) => {
    const usersToBlock = users
      .filter(
        (user: IUser) =>
          userIds?.includes(user.uuid) && user.isRegisterBlocked !== true,
      )
      .map((user: IUser) => user.uuid);

    if (usersToBlock.length > 0) {
      setBlockingUserIds(usersToBlock);
      setToggleConfirmUserBlockingDialog(true);
    }
  };

  const handleToggleUnblockingDialog = (userIds: string[]) => {
    const usersToUnblock = users
      .filter((user: IUser) => userIds?.includes(user.uuid))
      .map((user: IUser) => user.uuid);

    if (usersToUnblock.length > 0) {
      setUnblockingUserIds(usersToUnblock);
      setToggleConfirmUserUnblockingDialog(true);
    }
  };

  const showBlockButton = selectAllChecked || selectedUsers.length > 0;
  const allSelectedBlocked = selectedUsers.every((userId) => {
    const user = users.find((u: IUser) => u.uuid === userId);
    return user?.isRegisterBlocked === true;
  });

  const handleCloseBlockingDialog = () => {
    setBlockingUserIds([]);
    setToggleConfirmUserBlockingDialog(false);
  };

  const handleCloseSuccessDialog = () => {
    setOperationSuccessDialog(false);
  };

  const handleBlockUsers = async () => {
    const Resp = await blockUsers(blockingUserIds, blockingReason);
    setBlockingReason('');
    handleCloseBlockingDialog();

    if (Resp.Status === 'OK') {
      setSelectAllChecked(false);
      setSelectedUsers([]);
      setOperationType('block');
      setOperationSuccessDialog(true);
      await getUsers(filters as UserListQuery);
    } else {
      alert(Resp.Mensagem);
    }
  };

  const applySort = () => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      sort: selectedSort,
    }));
    setToggleOrderDialog(false);
  };

  const applyFilters = async (
    name = '',
    cpf = '',
    selectedGroup = '',
    selectedProfile = '',
    selectedPermission = '',
    userStatus = '',
  ) => {
    try {
      const newFilters: Partial<UserListQuery> = {
        ...filters,
        nome: name || undefined,
        cpf: cpf || undefined,
        groupUuid: selectedGroup || undefined,
        profileUuid: selectedProfile || undefined,
        permissionUuid: selectedPermission || undefined,
        page: '1',
        statusName: userStatus || undefined,
      };

      Object.keys(newFilters).forEach(key => {
        if (
          newFilters[key as keyof UserListQuery] === undefined ||
          newFilters[key as keyof UserListQuery] === ''
        ) {
          delete newFilters[key as keyof UserListQuery];
        }
      });

      setName(name);
      setCpf(cpf);
      setSelectedGroup(selectedGroup);
      setSelectedProfile(selectedProfile);
      setSelectedPermission(selectedPermission);
      setSelectedUserStatus(userStatus);

      setFilters(newFilters);
      setCurrentPage(1);


      return true;
    } catch (error) {
      console.error('Error applying filters:', error);
      setInvalidRequest(true);
      return false;
    }
  };
  const handleToggleOrderDialog = () => {
    setToggleOrderDialog(!toggleOrderDialog);
  };

  const handleToggleFiltersDialog = () => {
    setToggleFiltersDialog(!toggleFiltersDialog);
  };

  const handleSortChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedSort(event.target.value);
  };

  const handleClearFilters = () => {
    setFilters({ page: '1', limit: '10', sort: 'DESC' });

    setName('');
    setCpf('');
    setSelectedGroup('');
    setSelectedProfile('');
    setSelectedPermission('');
    setSelectedUserStatus('');

    setCurrentPage(1);

    localStorage.removeItem('userFilters');
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number,
  ) => {
    setCurrentPage(page);
    setFilters((prevFilters) => ({
      ...prevFilters,
      page: String(page),
    }));
  };

  const handleCloseDialog = () => {
    setInvalidRequest(false);
  };

  const handleCloseManageUserTermDialog = () => {
    setManageUserTermDialog({
      open: false,
      userUuid: '',
      userName: '',
      close: () =>
        setManageUserTermDialog((prev) => ({ ...prev, open: false })),
    });
  };

  const handleRefresh = () => {
    window.location.reload();
    setInvalidRequest(false);
  };

  const loadFiltersFromLocalStorage = (): UserListQuery | null => {
    const savedFilters = localStorage.getItem('userFilters');
    if (savedFilters) {
      const parsed = JSON.parse(savedFilters);
      // Validar que limit seja um valor válido
      const validLimits = ['5', '10', '25', '50', 'all'];
      if (parsed.limit && !validLimits.includes(parsed.limit)) {
        parsed.limit = '10';
      }
      return parsed;
    }
    return null;
  };

  useEffect(() => {
    if (!hasPermission(ModuleEnum.USERS, PermissionType.LIST)) {
      alert('Você não tem permissão para acessar esta página.');
      navigate(ROUTES.HOME);
    }
  }, []);

  useEffect(() => {
    getUsers(filters as UserListQuery);
  }, [filters]);

  const totalPages = useMemo(() => {
    if (filters.limit === 'all') {
      return 1;
    }
    const limit = Number(filters.limit) || 10;
    const count = infoUsers?.count || 0;
    return Math.max(1, Math.ceil(count / limit));
  }, [filters.limit, infoUsers?.count]);

  const getSelectValue = () => {
    if (filters.limit === 'all') {
      return 'all';
    }
    return filters.limit ?? '10';
  };

  const handleLimitChange = (newLimit: any) => {
    let limitValue: string;

    if (newLimit === 'all') {
      limitValue = 'all';
    } else {
      limitValue = String(newLimit);
    }

    const newFilters = {
      ...filters,
      limit: limitValue,
      page: '1',
    };
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const renderSelectValue = (selected: any) => {
    return selected === 'all' ? 'Todos' : selected;
  };

  const getSuccessMessage = () => {
    return operationType === 'block'
      ? 'Usuário(s) bloqueado(s) com sucesso!'
      : 'Usuário(s) desbloqueado(s) com sucesso!';
  };

  return (
    <ContainerPainel pagina='Gestão de Usuários' showBackButton={true}>
      <VerificaBloqueado usersData={users} />
      <Box
        sx={{
          backgroundColor: '#0048B6',

          alignContent: 'center',
        }}
      >
        <Box
          sx={{
            backgroundColor: '#0048B6',
            height: '190px',
            display: 'flex',
            justifyContent: 'center',
            marginTop: '25px',
          }}
        >
          <Box sx={{ width: '1552px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Chip
                  label='Ordernar'
                  onClick={handleToggleOrderDialog}
                  onDelete={handleToggleOrderDialog}
                  deleteIcon={
                    <ArrowDropDownIcon style={{ color: '#FFFFFF' }} />
                  }
                  variant='outlined'
                  sx={{
                    color: '#FFFFFF',
                    border: '1px solid #FFFFFF',
                    marginRight: '10px',
                  }}
                />
                <Chip
                  label='Filtrar'
                  onClick={handleToggleFiltersDialog}
                  onDelete={handleToggleFiltersDialog}
                  deleteIcon={
                    <ArrowDropDownIcon
                      style={{
                        color: hasAppliedFilters() ? '#0048B6' : '#FFFFFF',
                      }}
                    />
                  }
                  variant='outlined'
                  sx={{
                    color: hasAppliedFilters() ? '#0048B6' : '#FFFFFF',
                    backgroundColor: hasAppliedFilters()
                      ? '#FFFFFF'
                      : '#0048B6',
                    border: '1px solid #FFFFFF',
                  }}
                />
                {hasAppliedFilters() && (
                  <Button
                    variant='text'
                    startIcon={<CloseIcon />}
                    sx={{ color: '#FFFFFF', marginLeft: '10px' }}
                    onClick={handleClearFilters}
                  >
                    Limpar filtros
                  </Button>
                )}
              </Box>
              {hasPermission(ModuleEnum.USERS, PermissionType.CREATE) && (
                <Button
                  variant='outlined'
                  startIcon={<AddIcon />}
                  sx={{ color: '#FFFFFF', border: '1px solid #FFFFFF' }}
                  onClick={() => navigate(ROUTES.USER_CREATE)}
                >
                  CADASTRAR USUÁRIO
                </Button>
              )}
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                paddingBottom: '10px',
              }}
            >
              <Checkbox
                sx={{ color: '#FFFFFF' }}
                checked={selectAllChecked}
                onChange={handleSelectAll}
              />
              <Typography
                sx={{
                  color: '#FFFFFF',
                  fontSize: '18px',
                  lineHeight: '27px',
                }}
              >
                Selecionar todos | {selectedUsers.length} de{' '}
                {infoUsers?.count || 0} selecionado(s)
              </Typography>
              {showBlockButton &&
                !allSelectedBlocked &&
                hasPermission(ModuleEnum.USERS, PermissionType.UPDATE) && (
                  <Button
                    variant='outlined'
                    startIcon={<LockOutlinedIcon />}
                    onClick={() => handleToggleBlockingDialog(selectedUsers)}
                    sx={{
                      color: '#FFFFFF',
                      border: '1px solid #FFFFFF',
                      marginLeft: '15px',
                    }}
                  >
                    BLOQUEAR SELECIONADO(S)
                  </Button>
                )}
            </Box>

            {users?.map((user: IUser) => {
              const managePermission = [
                'REPRESENTANTE LEGAL MUNICIPAL',
              ].includes(user?.permission?.name || '');

              let hasValidValidity = false;

              if (managePermission) {
                hasValidValidity = user!.validity?.isActive || false;
              }


              return (
                <UserCard
                  key={user?.uuid}
                  userUuid={user?.uuid}
                  name={user?.name}
                  cpf={user?.identification}
                  city={user?.address?.municipality || ''}
                  group={user?.permission?.profile?.group?.name || ''}
                  profile={user?.permission?.profile?.name}
                  permission={user?.permission?.name}
                  status={user?.isRegisterActive && !user?.isRegisterBlocked}
                  blocked={user?.isRegisterBlocked}
                  isChecked={selectedUsers?.includes(user?.uuid)}
                  onCheckboxChange={handleUserSelect(user?.uuid)}
                  onBlockUser={() => handleToggleBlockingDialog([user?.uuid])}
                  onUnblockUser={() =>
                    handleToggleUnblockingDialog([user?.uuid])
                  }
                  hasValidValidity={hasValidValidity}
                  isRepresentativeLegalMunicipal={managePermission}
                  blockDisabled={user?.isRegisterBlocked}
                  usersWithTermValidity={managePermission}
                  openManageTermDialog={() =>
                    setManageUserTermDialog({
                      open: true,
                      userUuid: user.uuid,
                      userName: user.name,
                      close: handleCloseManageUserTermDialog,
                    })
                  }
                />
              );
            })}

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: '20px',
                paddingBottom: '20px',
                gap: 2,
              }}
            >
              {totalPages > 1 && (
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  color='primary'
                  onChange={handlePageChange}
                  showFirstButton
                  showLastButton
                  siblingCount={1}
                  boundaryCount={1}
                  disabled={false}
                />
              )}
              <Box display='flex' alignItems='center' gap={1}>
                <Typography variant='body2'>
                  Total: {infoUsers?.count || 0} registro(s) | Itens por página:
                </Typography>
                <Select
                  value={getSelectValue()}
                  onChange={(e) => handleLimitChange(e.target.value)}
                  size='small'
                  renderValue={renderSelectValue}
                >
                  {[5, 10, 25, 50].map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                  <MenuItem key='all' value='all'>
                    Todos
                  </MenuItem>
                </Select>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <OrderDialog
        open={toggleOrderDialog}
        selectedSort={selectedSort}
        onClose={() => setToggleOrderDialog(false)}
        handleSortChange={handleSortChange}
        applySort={applySort}
      />

      <FiltersDialog
        open={toggleFiltersDialog}
        onClose={() => setToggleFiltersDialog(false)}
        applyFilters={applyFilters}
        groupsDropdown={groupsDropdown}
        profilesDropdown={profilesDropdown}
        permissionsDropdown={permissionsDropdown}
        userStatusDropdown={userStatusDropdown}
        initialName={name}
        initialCpf={cpf}
        initialGroup={selectedGroup}
        initialProfile={selectedProfile}
        initialPermission={selectedPermission}
        initialUserStatus={selectedUserStatus}
        initialMunicipality={'selectedMunicipality'}
      />

      <Dialog
        open={toggleConfirmUserBlockingDialog}
        onClose={handleCloseBlockingDialog}
        fullWidth
        maxWidth='md'
      >
        <DialogTitle sx={{ textAlign: 'center' }}>
          <img src={LockRedIcon} alt='Lock Icon' />
          <Typography sx={{ fontSize: '24px', marginTop: '20px' }}>
            Bloquear Usuário(s)
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ textAlign: 'center' }}>
            Tem certeza que você deseja bloquear o(s) Usuário(s)?
          </DialogContentText>

          {/* input text com a justificativa */}
          <TextField
            label='Justificativa'
            value={blockingReason}
            onChange={(e) => setBlockingReason(e.target.value)}
            sx={{ width: '100%', marginTop: '20px' }}
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', marginBottom: '30px' }}>
          <Button
            variant='contained'
            onClick={handleBlockUsers}
            sx={{
              backgroundColor: '#D32F2F',
              '&:hover': { backgroundColor: '#D32F2F' },
            }}
          >
            CONFIRMAR BLOQUEIO
          </Button>
          <Button onClick={handleCloseBlockingDialog}>VOLTAR</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={toggleConfirmUserUnblockingDialog}
        onClose={handleCloseUnblockingDialog}
        fullWidth
        maxWidth='md'
      >
        <DialogTitle sx={{ textAlign: 'center' }}>
          <img src={UpdateIcon} alt='Lock Icon' />
          <Typography sx={{ fontSize: '24px', marginTop: '20px' }}>
            Desbloquear Usuário(s)
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ textAlign: 'center' }}>
            Tem certeza que você deseja desbloquear o(s) Usuário(s)?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', marginBottom: '30px' }}>
          <Button
            variant='contained'
            onClick={handleUnblockUsers}
            sx={{
              backgroundColor: '#0048B6',
              '&:hover': { backgroundColor: '#0048B6' },
            }}
          >
            CONFIRMAR DESBLOQUEIO
          </Button>
          <Button onClick={handleCloseUnblockingDialog}>VOLTAR</Button>
        </DialogActions>
      </Dialog>

      <SuccessDialog
        open={operationSuccessDialog}
        onClose={handleCloseSuccessDialog}
        message={getSuccessMessage()}
      />

      <ManageUserTerm manageUserTermDialog={manageUserTermDialog} />

      <ErrorDialog
        open={invalidRequest}
        title='Ocorreu um erro na sua solicitação'
        message1='Deseja atualizar a página atual?'
        button1Text='FECHAR'
        button1Action={handleCloseDialog}
        button1Color='inherit'
        button2Text='ATUALIZAR'
        button2Action={handleRefresh}
        button2Color='primary'
      />
    </ContainerPainel>
  );
};

export default ManageUser;
